import React, { useState, useEffect, useCallback } from 'react';
import { getCsrfToken } from '../utils/auth';

// The COLUMNS of one Excel sheet (2026-10-27, backend doc_import_columns.py): what each column is —
// the item's name, its description (several columns, "Header: value" per line), an Orcanos field,
// the old ID, the paragraph, trace IDs or not needed. Suggested from the names, then by the AI
// against the level's Orcanos fields; every change is saved at once. A list field's values are
// matched to the Orcanos options (a similar word is treated as the same); a value not in the list
// is asked about. Saved, the AI reads only the columns it needs — or none, with "Each row is one item".

// A ? that opens a short explanation on hover / focus.
function Help({ label, children }) {
  return (
    <span className="di-help" tabIndex={0} role="button" aria-label={label}>?
      <span className="di-help-tip" role="tooltip">{children}</span>
    </span>
  );
}

const USES = [
  ['name', 'Item name'], ['text', 'Description'], ['field', 'Orcanos field'], ['id', 'Old ID'],
  ['paragraph', 'Paragraph / module'], ['trace', 'Trace IDs'], ['skip', 'Not needed'],
];
const HOW = {
  exact: null, word: null, number: null,
  similar: ['di-chip-purple', 'Similar — treated as the same'],
  ai: ['di-chip-purple', 'AI: means the same'],
  chosen: ['di-chip-ok', 'Chosen by you'],
  empty: ['', 'Left empty'],
  missing: ['di-chip-amber', 'Not in the Orcanos list'],
};

async function call(url, method = 'GET', data) {
  const opts = { method, credentials: 'include' };
  if (data) {
    opts.headers = { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() };
    opts.body = JSON.stringify(data);
  }
  const res = await fetch(url, opts);
  let body = null;
  try { body = await res.json(); } catch (e) { body = null; }
  if (!res.ok) throw new Error((body && (body.detail || body.error)) || `Request failed (${res.status})`);
  return body;
}

const EMPTY = '__empty__';
const pct = (a, b) => (b ? Math.round((1 - a / b) * 100) : 0);

export default function SheetColumns({ base, fileId, table, locked, setError, onSaved }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState('');
  const url = `${base}/files/${fileId}/columns`;

  const load = useCallback(async () => {
    try { setD(await call(`${url}?table=${encodeURIComponent(table.id)}`)); } catch (e) { setError(e.message); }
  }, [url, table.id, setError]);
  useEffect(() => { load(); }, [load]);

  const save = async (patch, label = 'save') => {
    setBusy(label);
    try { setD(await call(url, 'PUT', { table: table.id, ...patch })); onSaved && onSaved(); }
    catch (e) { setError(e.message); }
    finally { setBusy(''); }
  };
  const askAi = async () => {
    setBusy('ai');
    try { setD(await call(`${url}/ai`, 'POST', { table: table.id })); } catch (e) { setError(e.message); }
    finally { setBusy(''); }
  };

  if (!d) return <div className="di-muted di-small">Reading the columns…</div>;
  const listFields = d.fields.filter(f => f.options?.length && !['text', 'textArea', 'textarea', 'richEditor', 'html', 'memo', 'numeric'].includes(f.type));
  const isList = title => listFields.some(f => f.title === title);
  const cols = d.columns;
  const setCol = (col, use, field = '') => {
    const next = cols.map(c => (c.col === col ? { col, use, field: use === 'field' ? field : '' } : { col: c.col, use: c.use, field: c.field }));
    if (use === 'field' && !field) return setD({ ...d, columns: cols.map(c => (c.col === col ? { ...c, use, field: '' } : c)) });
    save({ columns: next });
  };
  const choose = (field, value, option) => save({ value_choices: { [field]: { [value]: option } } });
  const fieldsTaken = new Set(cols.filter(c => c.use === 'field' && isList(c.field)).map(c => c.field));
  const stateChip = d.auto_rows
    ? <span className="di-chip di-chip-purple">Set for you — each row is one item · check, then confirm</span>
    : d.state === 'saved'
    ? <span className="di-chip di-chip-ok">Saved</span>
    : <span className="di-chip di-chip-purple">{d.state === 'ai' ? 'Suggested by the AI' : 'Suggested from the column names'} — confirm</span>;
  const reading = d.rows_items && d.mapped
    ? 'Each row becomes one item — the AI does not read this sheet.'
    : `The AI reads ${cols.filter(c => ['name', 'text', 'id', 'paragraph', 'trace'].includes(c.use)).length} of ${cols.length} columns`
      + (d.chars.all ? ` — ${pct(d.chars.ai, d.chars.all)}% less text.` : '.');

  return (
    <div className="di-cols">
      <div className="di-guide-row">
        <span className="di-label">Columns of {d.table.anchor}</span>
        <span className="di-muted di-small">{d.table.rows} rows{d.level_label ? ` → ${d.level_label}` : ''}</span>
        {stateChip}
        <span className="di-cols-actions">
          <button type="button" className="di-btn di-btn-s" disabled={locked || !!busy} onClick={askAi}
            title="The AI compares the sheet's columns and values with the Orcanos fields of this level">
            {busy === 'ai' ? 'Asking…' : '✨ Ask the AI'}</button>
          {(d.state !== 'saved' || d.auto_rows) && (
            <button type="button" className="di-btn di-btn-p di-btn-s" disabled={locked || !!busy}
              onClick={() => save({})}>Use these columns</button>)}
        </span>
      </div>
      {d.ai_note && <div className="di-muted di-small">{d.ai_note}</div>}
      {d.form_error && <div className="di-error di-small">{d.form_error}</div>}
      {!d.block_id && <div className="di-muted di-small">Choose this file's level (or answer which level its rows are) to map columns to Orcanos fields.</div>}

      <div className="di-scroll-x">
        <table className="di-cols-table">
          <thead><tr><th>Column</th><th>Example</th><th>Use as</th><th>Why</th></tr></thead>
          <tbody>
            {cols.map(c => (
              <tr key={c.col} className={c.use === 'skip' ? 'off' : ''}>
                <td><b>{c.header || `(column ${c.col})`}</b><div className="di-muted di-small">{c.filled} filled · {c.distinct} different</div></td>
                <td className="di-cols-sample" title={(c.samples || []).join(' | ')}>{(c.samples || []).slice(0, 2).join(' | ') || '—'}</td>
                <td>
                  <select value={c.use} disabled={locked || !!busy} aria-label={`Use of ${c.header}`}
                    onChange={e => setCol(c.col, e.target.value, e.target.value === 'field' ? (c.field || '') : '')}>
                    {USES.filter(([k]) => k !== 'field' || d.fields.length).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
                  </select>
                  {c.use === 'field' && (
                    <select value={c.field || ''} disabled={locked || !!busy} aria-label={`Orcanos field for ${c.header}`}
                      onChange={e => setCol(c.col, 'field', e.target.value)}>
                      {!c.field && <option value="">Choose the field…</option>}
                      {[...d.fields].sort((a, b) => a.title.localeCompare(b.title)).map(f => (
                        <option key={f.title} value={f.title} disabled={isList(f.title) && fieldsTaken.has(f.title) && f.title !== c.field}>
                          {f.title}{isList(f.title) ? ' (list)' : ''}{f.mandatory ? ' *' : ''}</option>))}
                    </select>
                  )}
                </td>
                <td className="di-muted di-small">{c.why}{c.by === 'ai' ? ' · AI' : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="di-muted di-small">Several columns can go into the description — each is written as “Header: value”. A list field takes one column.</div>

      <div className="di-check di-small">
        <label className="di-check">
          <input type="checkbox" checked={!!d.rows_items} disabled={locked || !!busy || !d.block_id || (!!d.notes && !d.rows_items)}
            onChange={e => save({ rows_items: e.target.checked })} />
          <b>Each row is one item</b>
        </label>
        <Help label="What does Each row is one item mean?">
          <span><b>Ticked</b> — every row is copied as one item, word for word. The AI does not read the rows: cheaper, exact and fast. Right for a sheet with one requirement / test per row.</span>
          <span><b>Unticked</b> — the AI reads the rows and may split a row with two “shall”s into two items, join a requirement wrapped over several rows, skip a note or heading row, or reword a row written as a remark.</span>
        </Help>
      </div>
      {d.notes && <div className="di-warn di-small">{d.notes} — skip this sheet; its rows are not requirements.</div>}
      <div className="di-small"><b>{reading}</b></div>

      {d.prefixes?.length > 0 && (
        // Rows of another level hiding in the sheet (2026-10-27): a software sheet held 16 `SYS-` product
        // rows, cited by the others as parents — all 16 became software items.
        <div className={`di-cols-values ${d.prefixes.some(p => p.parent_like && !d.skip_prefixes.includes(p.prefix)) ? 'need' : ''}`}>
          <div className="di-guide-row">
            <span className="di-label">The IDs start differently</span>
            <Help label="What does The IDs start differently mean?">
              <span>The old IDs in this sheet start with more than one prefix — here: {d.prefixes.map(p => `${p.prefix}-`).join(', ')}. Often that means rows of another level sit in the same sheet (e.g. product <b>SYS-</b> rows in a software sheet).</span>
              <span>Every ticked prefix becomes {d.level_label || 'an item of this level'}. <b>Untick</b> a prefix to leave its rows out — import them on their own level instead.</span>
            </Help>
            <span className="di-muted di-small">Every row becomes {d.level_label || 'an item of this level'} — leave out rows that belong to another level.</span>
          </div>
          <div className="di-cols-vals">
            {d.prefixes.map(p => {
              const out = d.skip_prefixes.includes(p.prefix);
              return (
                <label key={p.prefix} className="di-check di-small di-prefix">
                  <input type="checkbox" checked={!out} disabled={locked || !!busy}
                    onChange={e => save({ skip_prefixes: e.target.checked ? d.skip_prefixes.filter(x => x !== p.prefix) : [...d.skip_prefixes, p.prefix] })} />
                  <span><b>{p.prefix}-</b> × {p.count} <span className="di-muted">rows {p.rows.join(', ')}{p.count > p.rows.length ? ' …' : ''}</span>
                    {out && <span className="di-chip">left out</span>}
                    {p.parent_like && !out && <span className="di-chip di-chip-amber">other rows trace to these — parents?</span>}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {d.values.map(v => (
        <div key={v.field} className={`di-cols-values ${v.missing ? 'need' : ''}`}>
          <div className="di-guide-row">
            <span className="di-label">Values of “{v.header}” → {v.field}</span>
            {v.missing > 0
              ? <span className="di-chip di-chip-amber">{v.missing} not in the Orcanos list</span>
              : <span className="di-chip di-chip-ok">All values matched</span>}
          </div>
          {v.too_many ? <div className="di-muted di-small">{v.too_many} different values — this column does not look like a list. Map it to a text field or the description.</div> : (
            <>
              {v.missing > 0 && (
                <div className="di-muted di-small">
                  A value marked <b>Not in the Orcanos list</b> can't be written to {v.field}. Add it to the list in Orcanos and press <b>Check again</b>,
                  choose an existing value instead, or leave it empty.
                  <button type="button" className="di-btn di-btn-s" disabled={!!busy} onClick={load}>Check again</button>
                </div>
              )}
              <div className="di-cols-vals">
                {v.values.map(x => {
                  const chip = HOW[x.how];
                  return (
                    <div key={x.value} className="di-cols-val">
                      <span className="di-cols-from">{x.value} <span className="di-muted">×{x.count}</span></span>
                      <span aria-hidden="true">→</span>
                      <select value={x.option || ''} disabled={locked || !!busy} aria-label={`Value for ${x.value}`}
                        onChange={e => choose(v.field, x.value, e.target.value === EMPTY ? '' : e.target.value)}>
                        {x.how === 'missing' && <option value="" disabled>Not in the list — choose…</option>}
                        <option value={x.how === 'missing' ? EMPTY : ''}>(leave empty)</option>
                        {v.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                      {chip && <span className={`di-chip ${chip[0]}`}>{chip[1]}</span>}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}
